import { Loadstone, LoadstoneConfig, LoadstoneProps } from '@highlight/ui'

// Staggered so the grid ripples rather than blinking as one block.
const CELL_DELAYS_MS = [0, 120, 240, 120, 240, 360, 240, 360, 480]

/**
 * In-flight generation status — a rippling dot grid. Purely decorative; the
 * label beside it carries the meaning, so callers name what is being generated
 * ("Preparing" for the summary, "Generating brief" for a brief).
 */
export function PreparingIndicator({ label = 'Preparing' }: { label?: string }) {
  return (
    <span className="text-foreground-secondary type-ui-xs flex items-center gap-1.5 font-medium">
      {label}
      <span aria-hidden="true" className="grid grid-cols-3 gap-[2px]">
        {CELL_DELAYS_MS.map((delay, cell) => (
          <span
            key={cell}
            className="bg-foreground-secondary corner-smooth size-[3px] rounded-[1px] motion-safe:animate-pulse"
            style={{ animationDelay: `${delay}ms`, animationDuration: '1.2s' }}
          />
        ))}
      </span>
    </span>
  )
}

// Full 4×4 grid, left-to-right "reading" sweep (the studio's Reading preset,
// recolored to the meeting amber).
const readingCells = Array.from({ length: 16 }, (_, order) => ({
  row: Math.floor(order / 4),
  column: order % 4,
  order,
  lightColor: '#7c5cff',
  darkColor: '#f59e0b',
  opacity: 0.18,
  scale: 0.45,
}))

const readingOrangeConfig = {
  rows: 4,
  columns: 4,
  animation: 'pulse',
  duration: 900,
  stagger: 110,
  gap: 3,
  radius: 3,
  ledMode: false,
  cells: readingCells,
} satisfies LoadstoneConfig

/** The amber "reading" grid — the meeting brief's preparing state. */
export function ReadingOrangeLoadstone(props: Omit<LoadstoneProps, 'config'>): JSX.Element {
  return <Loadstone config={readingOrangeConfig} {...props} />
}
